import { useState } from 'react';
import { Copy } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { ContactTab } from './contact-tab';
import { AboutTab } from './about-tab';
import { MediaTab } from './media-tab';
import { Icons } from '@/components/icons';
import { ProjectsTab } from './projects-tab';
import type { Organisation } from '@/services/types';
import { showToast } from '@/components/ui/show-toast';
import { ENV } from '@/constants';

type dataPanelProps = {
  data: Organisation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type TabType = 'about' | 'contact' | 'history' | 'media' | 'projects';

export function ProfilePanel({ data, open, onOpenChange }: dataPanelProps) {
  const [activeTab, setActiveTab] = useState<TabType>('about');

  // Generate public profile URL
  const publicProfileUrl = `${ENV.PROFILE_URL}/pr/${data.slug}`;

  const handleCopyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(publicProfileUrl);
      showToast('success', 'Profile link copied to clipboard');
    } catch (err) {
      showToast('error', 'Failed to copy link');
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full max-w-md overflow-y-auto p-0 sm:max-w-lg'>
        <div className='flex h-full flex-col'>
          <SheetHeader className='flex flex-row items-start p-4 pb-0'>
            <div className='flex flex-1 items-center gap-3'>
              {data?.logo && data?.logo !== '' ? (
                <div className='relative h-12 w-12 overflow-hidden rounded border'>
                  <img
                    src={data.logo}
                    alt={`${data.name} logo`}
                    className='h-full w-full object-contain'
                  />
                </div>
              ) : (
                <div className='flex h-12 w-12 items-center justify-center rounded bg-muted'>
                  {data?.name?.charAt(0)}
                </div>
              )}
              <div className='min-w-0'>
                <div className='flex min-w-0 items-center gap-2'>
                  <SheetTitle className='min-w-0 truncate whitespace-normal break-words text-xl'>
                    {data?.name}
                  </SheetTitle>
                  {data?.emailVerified && <Icons.verified />}
                </div>
                <div className='flex items-center gap-2'>
                  <p className='text-xs text-muted-foreground truncate'>
                    Profile:{' '}
                    <a
                      href={publicProfileUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary hover:underline'
                      onClick={(e) => e.stopPropagation()}
                    >
                      {publicProfileUrl.length > 25
                        ? `${publicProfileUrl.substring(0, 30)}...`
                        : publicProfileUrl}
                    </a>
                  </p>
                  <button
                    onClick={handleCopyProfileLink}
                    className='flex-shrink-0 flex items-center justify-center rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                    aria-label='Copy profile link'
                    title='Copy profile link'
                  >
                    <Copy className='h-3.5 w-3.5' />
                  </button>
                </div>
              </div>
            </div>
          </SheetHeader>

          <div className='mt-4 border-b'>
            <div className='flex overflow-x-auto'>
              {['About', 'Contact', 'Media', 'Projects'].map((tab) => {
                const tabKey = tab.toLowerCase().replace(/\s+/g, '') as TabType;
                return (
                  <Button
                    key={tab}
                    variant='ghost'
                    className={cn(
                      'rounded-none px-4 py-2 text-sm font-medium',
                      activeTab === tabKey
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-muted-foreground'
                    )}
                    onClick={() => setActiveTab(tabKey)}
                  >
                    {tab}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className='w-full flex-1 flex-wrap overflow-y-auto p-4'>
            {activeTab === 'about' && <AboutTab data={data} />}
            {activeTab === 'contact' && <ContactTab data={data} />}
            {/* {activeTab === 'history' && <HistoryTab />} */}
            {activeTab === 'media' && <MediaTab data={data} />}
            {activeTab === 'projects' && <ProjectsTab companyId={data.id} />}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
